import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Lock,
  Unlock,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Calendar,
  FileText,
  RefreshCw,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ClosingDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getClosingDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Closing</h1>
            <p className="text-gray-500">
              Fiscal Year {dashboard?.current_fiscal_year} - Period {dashboard?.current_period}
            </p>
          </div>
          <Button onClick={fetchDashboard} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Projects</p>
                  <p className="text-2xl font-bold">{dashboard?.active_projects || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">TECO Projects</p>
                  <p className="text-2xl font-bold">{dashboard?.teco_projects || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Lock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Closed Projects</p>
                  <p className="text-2xl font-bold">{dashboard?.closed_projects || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-rose-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unsettled Costs</p>
                  <p className="text-2xl font-bold">
                    ${(dashboard?.total_unsettled_costs || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Period Status & Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Period Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                Period Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <Unlock className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-700">{dashboard?.periods_open || 0}</p>
                    <p className="text-sm text-emerald-600">Open</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <Lock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-700">{dashboard?.periods_locked || 0}</p>
                    <p className="text-sm text-amber-600">Locked</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-orange-700">{dashboard?.periods_closed || 0}</p>
                    <p className="text-sm text-orange-600">Closed</p>
                  </div>
                </div>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => navigate('/ps/closing/periods')}
                  data-testid="manage-periods-btn"
                >
                  Manage Period Locks
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="h-20 flex-col"
                  onClick={() => navigate('/ps/closing/settlement')}
                  data-testid="settlement-btn"
                >
                  <DollarSign className="w-6 h-6 mb-1" />
                  <span className="text-xs">Settlement</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col"
                  onClick={() => navigate('/ps/closing/wip')}
                  data-testid="wip-btn"
                >
                  <TrendingUp className="w-6 h-6 mb-1" />
                  <span className="text-xs">WIP Calculation</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col"
                  onClick={() => navigate('/ps/closing/closure')}
                  data-testid="closure-btn"
                >
                  <Lock className="w-6 h-6 mb-1" />
                  <span className="text-xs">Project Closure</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-20 flex-col"
                  onClick={() => navigate('/ps/reports')}
                  data-testid="reports-btn"
                >
                  <FileText className="w-6 h-6 mb-1" />
                  <span className="text-xs">Reports</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Settlements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Recent Settlement Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_settlements?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Document</th>
                      <th className="text-left p-3">Project</th>
                      <th className="text-left p-3">Period</th>
                      <th className="text-right p-3">Costs Settled</th>
                      <th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recent_settlements.map((doc) => (
                      <tr key={doc.document_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-orange-600">{doc.document_number}</td>
                        <td className="p-3">{doc.project_name || doc.project_id}</td>
                        <td className="p-3">{doc.fiscal_year}/{doc.period}</td>
                        <td className="p-3 text-right font-medium">${(doc.total_costs_settled || 0).toLocaleString()}</td>
                        <td className="p-3">
                          <Badge className={
                            doc.status === 'POSTED' ? 'bg-emerald-100 text-emerald-700' :
                            doc.status === 'DRAFT' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {doc.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No recent settlements</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
